import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toastr from "toastr";
import {
  fetchClientInviteListWorkersSession,
  fetchClientSuggestedRemainingWorkersSession,
  fetchClientSuggestedReplacementWorkers,
  useInviteListAppend,
  useInviteListRemove,
  useManualInviteReplacementWorkers,
  useStartClientSmartMatching,
} from "../../../../api/booking";
import { isEligibleReplacementShift } from "../../replacementInviteEligibility";
import {
  isShiftAutomaticSmartMatchingRunning,
} from "../../replacementInviteEligibility";
import { getShiftInviteCap } from "../inviteCaps";
import {
  applyShiftsSnapshotToInviteMap,
  buildEmptyInviteListMap,
  dedupeWorkers,
  extractShiftSlice,
  getStep1ActionKey,
  getWorkerAvailableDates,
  getWorkerSlug,
  normalizeClientBookingPayload,
  SUGGESTED_WORKERS_PAGE_LIMIT,
} from "./useStep1SuggestedWorkers";

function getShiftPatternByResolvedIndex(shiftPattern, shiftIndex) {
  if (!Array.isArray(shiftPattern)) return null;
  return (
    shiftPattern.find(
      (p, i) => (typeof p?.shiftIndex === "number" ? p.shiftIndex : i) === shiftIndex
    ) ?? null
  );
}

function isReplacementInviteAlreadySent(pattern) {
  return pattern?.isFirstReplacementInvited === true;
}

/**
 * Replacement manual-invite flow: same invite-list / send behaviour as Step 1,
 * but suggested workers from replacement endpoints and no reject / no first-invite lock on suggestions.
 */
export function useReplacementSuggestedWorkers({ booking, bookingSlug, onAfterInvite }) {
  const inviteMut = useManualInviteReplacementWorkers();
  const appendMut = useInviteListAppend();
  const removeMut = useInviteListRemove();
  const startSmartMatchingMut = useStartClientSmartMatching();

  const [bootstrapKey, setBootstrapKey] = useState(0);
  const [byShift, setByShift] = useState({});
  const [selected, setSelected] = useState({});
  const [loadMorePending, setLoadMorePending] = useState({});
  const [initialPending, setInitialPending] = useState({});
  const [inviteListByShift, setInviteListByShift] = useState(null);
  const [inviteListSessionError, setInviteListSessionError] = useState(false);
  const [pendingAppendKey, setPendingAppendKey] = useState(null);
  const [pendingInviteShiftIndex, setPendingInviteShiftIndex] = useState(null);
  const [pendingSmartMatchShiftIndex, setPendingSmartMatchShiftIndex] = useState(null);
  const [pendingRemoveKey, setPendingRemoveKey] = useState(null);

  const byShiftRef = useRef({});
  useEffect(() => {
    byShiftRef.current = byShift;
  }, [byShift]);

  const bookingRef = useRef(booking);
  bookingRef.current = booking;

  const initDoneRef = useRef(new Set());

  const resetInitForBootstrap = useCallback(() => {
    initDoneRef.current = new Set();
  }, []);

  useEffect(() => {
    resetInitForBootstrap();
  }, [bootstrapKey, resetInitForBootstrap]);

  useEffect(() => {
    setPendingAppendKey(null);
    setPendingRemoveKey(null);
    setPendingInviteShiftIndex(null);
    setPendingSmartMatchShiftIndex(null);
  }, [bootstrapKey]);

  const patternsLen = booking?.shiftPattern?.length ?? 0;
  const inviteListWorkerSessionId = booking?.inviteListWorkerSessionId;

  const replacementShiftIndices = useMemo(() => {
    const patterns = booking?.shiftPattern ?? [];
    const out = [];
    patterns.forEach((p, i) => {
      if (!isEligibleReplacementShift(p)) return;
      if (isReplacementInviteAlreadySent(p)) return;
      out.push(typeof p.shiftIndex === "number" ? p.shiftIndex : i);
    });
    return out;
  }, [booking?.shiftPattern]);

  const markInitialPending = useCallback((shiftIndex, pending) => {
    setInitialPending((prev) => ({ ...prev, [shiftIndex]: pending }));
  }, []);

  const applyShiftSlice = useCallback((shiftIndex, res, { append } = { append: false }) => {
    const slice = extractShiftSlice(res, shiftIndex, {
      inferHasMoreFromChunkSize: append,
    });
    const { workers: chunk, matchingStatus, totalRecords } = slice;
    let { hasMore } = slice;

    setByShift((prev) => {
      const prevRow = prev[shiftIndex] || {};
      const base = append ? (prevRow.workers ?? []) : [];
      const merged = dedupeWorkers([...base, ...chunk]);
      if (totalRecords != null) {
        hasMore = merged.length < totalRecords;
      }
      return {
        ...prev,
        [shiftIndex]: {
          workers: merged,
          hasMore,
          matchingStatus: matchingStatus ?? prevRow.matchingStatus ?? null,
          initialLoaded: true,
        },
      };
    });
  }, []);

  const markShiftAutomaticSmartMatchingRunning = useCallback(
    (shiftIndex) => {
      markInitialPending(shiftIndex, false);
      setByShift((prev) => ({
        ...prev,
        [shiftIndex]: {
          workers: [],
          hasMore: false,
          initialLoaded: true,
          automaticSmartMatchingRunning: true,
        },
      }));
    },
    [markInitialPending]
  );

  const fetchReplacementPage = useCallback(
    (shiftIndex, { append }) => {
      const pattern = getShiftPatternByResolvedIndex(bookingRef.current?.shiftPattern, shiftIndex);
      if (isReplacementInviteAlreadySent(pattern)) return;
      if (isShiftAutomaticSmartMatchingRunning(pattern)) return;

      const req = append
        ? fetchClientSuggestedReplacementWorkers({
            bookingSlug,
            shiftIndex,
            limit: SUGGESTED_WORKERS_PAGE_LIMIT,
          })
        : fetchClientSuggestedReplacementWorkers({ bookingSlug, shiftIndex });

      req
        .then((res) => {
          applyShiftSlice(shiftIndex, res, { append });
          if (!append) markInitialPending(shiftIndex, false);
        })
        .catch(() => {
          if (!append) {
            markInitialPending(shiftIndex, false);
            setByShift((prev) => ({
              ...prev,
              [shiftIndex]: {
                ...(prev[shiftIndex] || {}),
                workers: [],
                hasMore: false,
                initialLoaded: true,
                suggestedFetchError: true,
              },
            }));
            toastr.error("Could not load suggested workers.");
          } else {
            toastr.error("Could not load more workers.");
          }
        })
        .finally(() => {
          if (append) {
            setLoadMorePending((p) => ({ ...p, [shiftIndex]: false }));
          }
        });
    },
    [applyShiftSlice, bookingSlug, markInitialPending]
  );

  useEffect(() => {
    const snap = bookingRef.current;
    if (!bookingSlug || !snap?.shiftPattern?.length) return;

    snap.shiftPattern.forEach((pattern, arrayIndex) => {
      if (!isEligibleReplacementShift(pattern)) return;
      const shiftIndex =
        typeof pattern.shiftIndex === "number" ? pattern.shiftIndex : arrayIndex;
      const initKey = `${bootstrapKey}:${shiftIndex}:${pattern?.invitedOrSmartMatchingStarted ? "auto-sm" : "ready"}`;
      if (initDoneRef.current.has(initKey)) return;

      if (isReplacementInviteAlreadySent(pattern)) {
        initDoneRef.current.add(initKey);
        markInitialPending(shiftIndex, false);
        setByShift((prev) => ({
          ...prev,
          [shiftIndex]: {
            workers: [],
            hasMore: false,
            initialLoaded: true,
            replacementInvitesSent: true,
          },
        }));
        return;
      }

      if (isShiftAutomaticSmartMatchingRunning(pattern)) {
        initDoneRef.current.add(initKey);
        markShiftAutomaticSmartMatchingRunning(shiftIndex);
        return;
      }

      const sessionId = pattern?.suggestedReplacementWorkerSessionId;

      if (sessionId) {
        initDoneRef.current.add(initKey);
        markInitialPending(shiftIndex, true);
        fetchClientSuggestedRemainingWorkersSession(sessionId)
          .then((res) => {
            applyShiftSlice(shiftIndex, res, { append: false });
            markInitialPending(shiftIndex, false);
          })
          .catch(() => {
            markInitialPending(shiftIndex, false);
            setByShift((prev) => ({
              ...prev,
              [shiftIndex]: {
                ...(prev[shiftIndex] || {}),
                workers: [],
                hasMore: false,
                initialLoaded: true,
                sessionError: true,
              },
            }));
            toastr.error("Could not restore suggested workers session.");
          });
      } else {
        initDoneRef.current.add(initKey);
        markInitialPending(shiftIndex, true);
        fetchReplacementPage(shiftIndex, { append: false });
      }
    });
  }, [
    bookingSlug,
    patternsLen,
    bootstrapKey,
    fetchReplacementPage,
    markInitialPending,
    markShiftAutomaticSmartMatchingRunning,
    applyShiftSlice,
  ]);

  useEffect(() => {
    if (!bookingSlug || !patternsLen) return;

    let cancelled = false;
    const sessionId = bookingRef.current?.inviteListWorkerSessionId;

    if (!sessionId) {
      setInviteListByShift(buildEmptyInviteListMap(patternsLen));
      setInviteListSessionError(false);
      return;
    }

    setInviteListByShift(null);
    setInviteListSessionError(false);

    fetchClientInviteListWorkersSession(sessionId)
      .then((res) => {
        if (cancelled) return;
        const data = normalizeClientBookingPayload(res);
        setInviteListByShift(applyShiftsSnapshotToInviteMap(data?.shifts, patternsLen));
        setInviteListSessionError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setInviteListSessionError(true);
        setInviteListByShift(buildEmptyInviteListMap(patternsLen));
        toastr.error("Could not load invite list.");
      });

    return () => {
      cancelled = true;
    };
  }, [bookingSlug, patternsLen, bootstrapKey, inviteListWorkerSessionId]);

  const handleSmartMatchingStarted = useCallback(
    (shiftIndex) => {
      const pattern = getShiftPatternByResolvedIndex(
        bookingRef.current?.shiftPattern,
        shiftIndex
      );
      if (!isEligibleReplacementShift(pattern)) return;
      if (isReplacementInviteAlreadySent(pattern)) return;

      const initKey = `${bootstrapKey}:${shiftIndex}:auto-sm`;
      if (initDoneRef.current.has(initKey)) return;
      initDoneRef.current.add(initKey);
      markShiftAutomaticSmartMatchingRunning(shiftIndex);
      setSelected((prev) => {
        if (!prev[shiftIndex]) return prev;
        const next = { ...prev };
        delete next[shiftIndex];
        return next;
      });
    },
    [bootstrapKey, markShiftAutomaticSmartMatchingRunning]
  );

  const loadMore = useCallback(
    (shiftIndex) => {
      const pattern = getShiftPatternByResolvedIndex(bookingRef.current?.shiftPattern, shiftIndex);
      if (isReplacementInviteAlreadySent(pattern)) return;
      if (isShiftAutomaticSmartMatchingRunning(pattern)) return;
      if (!byShiftRef.current[shiftIndex]?.hasMore) return;
      setLoadMorePending((p) => ({ ...p, [shiftIndex]: true }));
      fetchReplacementPage(shiftIndex, { append: true });
    },
    [fetchReplacementPage]
  );

  const toggleDate = useCallback((shiftIndex, workerSlug, isoDate) => {
    setSelected((prev) => {
      const row = { ...(prev[shiftIndex] || {}) };
      const dates = new Set(row[workerSlug] || []);
      if (dates.has(isoDate)) dates.delete(isoDate);
      else dates.add(isoDate);
      row[workerSlug] = [...dates];
      return { ...prev, [shiftIndex]: row };
    });
  }, []);

  const selectAllDatesForWorker = useCallback((shiftIndex, workerSlug) => {
    if (!workerSlug) return;
    const workers = byShiftRef.current[shiftIndex]?.workers ?? [];
    const w = workers.find((x) => getWorkerSlug(x) === workerSlug);
    const allDates = w ? getWorkerAvailableDates(w) : [];
    setSelected((prev) => {
      const row = { ...(prev[shiftIndex] || {}) };
      row[workerSlug] = [...allDates];
      return { ...prev, [shiftIndex]: row };
    });
  }, []);

  const addToInviteList = useCallback(
    (shiftIndex, workerSlug, workerDates) => {
      if (!workerSlug || !workerDates?.length) {
        toastr.warning("Select at least one date to add this worker to the invite list.");
        return;
      }
      const patternForShift = getShiftPatternByResolvedIndex(
        bookingRef.current?.shiftPattern,
        shiftIndex
      );
      if (isReplacementInviteAlreadySent(patternForShift)) {
        toastr.info("Replacement invites have already been sent for this shift.");
        return;
      }
      const cap = getShiftInviteCap(booking?.shiftPattern?.[shiftIndex]);
      const currentCount = inviteListByShift?.[shiftIndex]?.workers?.length ?? 0;
      if (cap > 0 && currentCount >= cap) {
        toastr.warning(`Shift ${shiftIndex + 1} invite list is full (${currentCount}/${cap}).`);
        return;
      }
      const key = getStep1ActionKey(shiftIndex, workerSlug);
      setPendingAppendKey(key);
      appendMut.mutate(
        {
          bookingSlug,
          workers: [{ shiftIndex, workerSlug, workerDates }],
          isReplacementInvite: true,
        },
        {
          onSettled: () => {
            setPendingAppendKey((cur) => (cur === key ? null : cur));
          },
          onSuccess: (res) => {
            const data = normalizeClientBookingPayload(res);
            if (data?.shifts) {
              setInviteListByShift(applyShiftsSnapshotToInviteMap(data.shifts, patternsLen));
            }
            toastr.success("Added to invite list.");
            setSelected((prev) => {
              const row = { ...(prev[shiftIndex] || {}) };
              delete row[workerSlug];
              return { ...prev, [shiftIndex]: row };
            });
          },
        }
      );
    },
    [appendMut, booking?.shiftPattern, bookingSlug, inviteListByShift, patternsLen]
  );

  const removeFromInviteList = useCallback(
    (shiftIndex, workerSlug) => {
      const key = getStep1ActionKey(shiftIndex, workerSlug);
      setPendingRemoveKey(key);
      removeMut.mutate(
        { bookingSlug, shiftIndex, workerSlug, isReplacementRemove: true },
        {
          onSettled: () => {
            setPendingRemoveKey((cur) => (cur === key ? null : cur));
          },
          onSuccess: (res) => {
            const data = normalizeClientBookingPayload(res);
            if (data && typeof data.shiftIndex === "number") {
              setInviteListByShift((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  [data.shiftIndex]: {
                    workerAlreadyInvited: !!data.workerAlreadyInvited,
                    workers: Array.isArray(data.workers) ? data.workers : [],
                  },
                };
              });
            }
            toastr.success("Removed from invite list.");
          },
        }
      );
    },
    [bookingSlug, removeMut]
  );

  const runSmartMatchingForShift = useCallback(
    (shiftIndex) => {
      if (!replacementShiftIndices.includes(shiftIndex)) {
        toastr.warning("This shift is not open for replacement smart matching right now.");
        return;
      }
      const inviteCount = inviteListByShift?.[shiftIndex]?.workers?.length ?? 0;
      if (inviteCount > 0) {
        toastr.warning(
          "Remove all workers from this shift’s invite list before running smart matching."
        );
        return;
      }
      setPendingSmartMatchShiftIndex(shiftIndex);
      startSmartMatchingMut.mutate(
        { bookingSlug, isReplacementMode: true },
        {
          onSettled: () => {
            setPendingSmartMatchShiftIndex((cur) => (cur === shiftIndex ? null : cur));
          },
          onSuccess: async () => {
            toastr.success("Smart matching started.");
            setBootstrapKey((k) => k + 1);
            setByShift({});
            setSelected({});
            await onAfterInvite?.();
          },
        }
      );
    },
    [
      bookingSlug,
      inviteListByShift,
      onAfterInvite,
      replacementShiftIndices,
      startSmartMatchingMut,
    ]
  );

  const inviteWorkersForShift = useCallback(
    (shiftIndex) => {
      if (!replacementShiftIndices.includes(shiftIndex)) {
        toastr.warning("This shift is not open for replacement invites right now.");
        return;
      }
      const row = inviteListByShift?.[shiftIndex];
      const count = row?.workers?.length ?? 0;
      if (!count) {
        toastr.warning("Add at least one worker to this shift’s invite list before sending.");
        return;
      }
      if (row?.workerAlreadyInvited) {
        toastr.info("Invites have already been sent for this shift.");
        return;
      }
      setPendingInviteShiftIndex(shiftIndex);
      inviteMut.mutate(
        { bookingSlug, shiftIndices: [shiftIndex], payload: {} },
        {
          onSettled: () => {
            setPendingInviteShiftIndex((cur) => (cur === shiftIndex ? null : cur));
          },
          onSuccess: () => {
            toastr.success("Invites sent.");
            setBootstrapKey((k) => k + 1);
            setByShift({});
            setSelected({});
            onAfterInvite?.();
          },
        }
      );
    },
    [bookingSlug, inviteListByShift, inviteMut, onAfterInvite, replacementShiftIndices]
  );

  const listBusy =
    appendMut.isPending ||
    removeMut.isPending ||
    inviteMut.isPending ||
    startSmartMatchingMut.isPending;

  const shiftInviteMeta = useMemo(() => {
    const patterns = booking?.shiftPattern ?? [];
    return patterns.map((pattern, shiftIndex) => {
      const cap = getShiftInviteCap(pattern);
      const currentCount = inviteListByShift?.[shiftIndex]?.workers?.length ?? 0;
      return {
        shiftIndex,
        cap,
        currentCount,
        isFull: cap > 0 && currentCount >= cap,
      };
    });
  }, [booking?.shiftPattern, inviteListByShift]);

  return {
    byShift,
    selected,
    inviteListByShift,
    inviteListSessionError,
    loadMore,
    loadMorePending,
    initialPending,
    toggleDate,
    selectAllDatesForWorker,
    addToInviteList,
    removeFromInviteList,
    inviteWorkersForShift,
    runSmartMatchingForShift,
    handleSmartMatchingStarted,
    startSmartMatchingMutation: startSmartMatchingMut,
    pendingSmartMatchShiftIndex,
    inviteMutation: inviteMut,
    pendingInviteShiftIndex,
    appendMutation: appendMut,
    removeMutation: removeMut,
    listBusy,
    pendingAppendKey,
    pendingRejectKey: null,
    pendingRemoveKey,
    bootstrapKey,
    isFirstInvited: false,
    shiftInviteMeta,
  };
}
